import React, { useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { EFP_COVER_CATEGORIES } from '@om/redux/actions/easiplusFuneralPlan/types';
import {
    getAddChild,
    getAddPartner,
    getAge,
    getAgeHintText,
    getAgePlaceholderText,
    getCoverAmount,
    getCoverAmountLimits,
    getSliderSteps,
    getDirectFamilyDrawerIsOpen,
    getDisplayCoverAmount,
    getDisplayMonthlyPremium,
    getShowingAgeValidation,
} from '@om/redux/selectors/easiplusFuneralPlan/quote';
import {
    setAddChildValue,
    setAddPartnerValue,
    setAgeValue,
    setCoverAmountValue,
    setShowingAgeValidation,
} from '@om/redux/actions/easiplusFuneralPlan/quote';
import { OmFormInputFieldWrapper, OmCheckBox, OmSlider, OmLoader } from '@om/component-library-react';
import QuoteSideDrawerWrapper from './QuoteSideDrawerWrapper';
import { DEFAULT_VALIDATION_DELAY } from '../../../config';
import {
    getIsQuoteReferenceDataValid,
    getIsApplicationReferenceDataValid,
} from '@om/redux/selectors/easiplusFuneralPlan/referenceData';

const FormContent = () => {
    const {
        directFamilyDrawerOpen,
        age,
        addPartner,
        addChild,
        coverAmount,
        displayCoverAmount,
        displayMonthlyPremium,
        coverAmountLimits,
        ageHintText,
        agePlaceholderText,
        showingAgeValidation,
        sliderSteps,
    } = useSelector(
        createStructuredSelector({
            directFamilyDrawerOpen: getDirectFamilyDrawerIsOpen,
            age: getAge,
            addPartner: getAddPartner,
            addChild: getAddChild,
            coverAmount: getCoverAmount,
            displayCoverAmount: getDisplayCoverAmount,
            displayMonthlyPremium: getDisplayMonthlyPremium,
            coverAmountLimits: getCoverAmountLimits,
            ageHintText: getAgeHintText,
            agePlaceholderText: getAgePlaceholderText,
            showingAgeValidation: getShowingAgeValidation,
            sliderSteps: getSliderSteps,
        })
    );

    const dispatch = useDispatch();
    return (
        <>
            <QuoteSideDrawerWrapper
                drawerIsOpen={directFamilyDrawerOpen}
                editCategory={EFP_COVER_CATEGORIES.DIRECT_FAMILY}
            >
                <h6 className="no-top-margin no-bottom-margin medium">Me & My Direct Family</h6>
                <OmFormInputFieldWrapper
                    id="age"
                    mask="number"
                    size="full"
                    align="left"
                    label=" "
                    required
                    keyboardMode="numeric"
                    placeholder={agePlaceholderText}
                    hintText={ageHintText}
                    validationDelay={DEFAULT_VALIDATION_DELAY}
                    value={age && age.value}
                    state={age && age.status}
                    errorMessage={age && age.error}
                    onShowingValidation={(event) => dispatch(setShowingAgeValidation(event.detail))}
                    onPersist={(event) => dispatch(setAgeValue(event.detail))}
                />

                {age && age.status === 'valid' && showingAgeValidation && (
                    <>
                        <h6 className="no-bottom-margin">Who would you like to include?</h6>
                        <OmCheckBox
                            id="addPartner"
                            text=" Add my Spouse/Partner"
                            checked={addPartner && addPartner.value}
                            onOnCheckBoxChanged={(event) => dispatch(setAddPartnerValue(event.detail))}
                        />
                        <OmCheckBox
                            id="addChild"
                            text=" Add my Child/Children"
                            checked={addChild && addChild.value}
                            onOnCheckBoxChanged={(event) => dispatch(setAddChildValue(event.detail))}
                        />

                        <h6 className="no-bottom-margin">Select cover amount:</h6>
                        <OmSlider
                            stepString={JSON.stringify(sliderSteps)}
                            minLabel={coverAmountLimits.minLabel}
                            maxLabel={coverAmountLimits.maxLabel}
                            value={coverAmount.value}
                            onSliderChange={(event) => dispatch(setCoverAmountValue(event.detail))}
                        />

                        <div className="cover-label-wrapper">
                            <span className="cover-label">
                                <h5 className="no-bottom-margin no-top-margin medium">{displayCoverAmount}</h5>
                                <h6 className="small medium no-top-margin">COVER AMOUNT</h6>
                            </span>

                            <span className="premium-label">
                                <h5 className="no-bottom-margin no-top-margin medium">{displayMonthlyPremium}</h5>
                                <h6 className="small medium no-top-margin">TOTAL MONTHLY PREMIUM</h6>
                            </span>
                        </div>
                    </>
                )}
                <div slot="modal-content"></div>
            </QuoteSideDrawerWrapper>
        </>
    );
};

const loadingText = (directFamilyDrawerOpen) => {
    return (
        <>
            <QuoteSideDrawerWrapper
                drawerIsOpen={directFamilyDrawerOpen}
                editCategory={EFP_COVER_CATEGORIES.DIRECT_FAMILY}
            >
                <OmLoader loaderText={'Loading...'} isLoading={true} />
            </QuoteSideDrawerWrapper>
        </>
    );
};

const DirectFamilyDrawer = () => {
    const { directFamilyDrawerOpen, quoteReferenceDataValid, applicationReferenceDataValid } = useSelector(
        createStructuredSelector({
            directFamilyDrawerOpen: getDirectFamilyDrawerIsOpen,
            quoteReferenceDataValid: getIsQuoteReferenceDataValid,
            applicationReferenceDataValid: getIsApplicationReferenceDataValid,
        })
    );

    if (applicationReferenceDataValid && quoteReferenceDataValid) {
        return <FormContent />;
    }

    return <div>{loadingText(directFamilyDrawerOpen)}</div>;
};

export default DirectFamilyDrawer;
